import { Content, Header } from '../../libs/gs-component-right-rail';
import React, { RefObject, useCallback, useEffect, useState } from 'react';

import { PageQuery, PagesFragment, usePagesQuery, usePageQuery, useUpdatePageMutation } from '../../generated/graphql';
import { REGISTRY_PAGE_TYPE, SCHEDULE_PAGE, WEDDING_PAGE_TYPE } from '../../helpers/pages';
import HelpfulIdeaCard from '../HelpfulIdeaCard';
import { AddPageContentRail } from './AddPageContentRail';
import { useHistory, useRouteMatch } from 'react-router-dom';

import styles from './addPageRail.scss';
import PageItemRoutes from '../PageItemsRail/PageItemRoutes';
import { addPagePreviewRoute, previewPageRoute, previewRoute } from '../../helpers/routes';
import AddPageButton from '../ManagePagesRail/AddPageButton';
import { AddPage } from '../ManagePagesRail/AddPage';
import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import { useApolloClient } from '@apollo/react-hooks';
import { pageQuery } from '../PageItemsRail/graphql';
import { useWebsiteLayoutContext } from '../../context/WebsiteLayoutContext';

interface Props {
  toggleAddPageHandler: () => void;
  iframeRef: RefObject<HTMLIFrameElement>;
  addPage?: PagesFragment;
  setCurrentPage: (page: PagesFragment) => void;
}

const updateIFrame = (pageData: PageQuery | null, iframeRef: React.RefObject<HTMLIFrameElement>) => {
  // update pagedata and selected page
  ['updatePage', 'currentPage'].forEach(messageType => {
    iframeRef?.current?.contentWindow?.postMessage(
      JSON.stringify({
        type: messageType,
        data: {
          ...pageData?.page,
        },
      }),
      getTargetOrigin()
    );
  });
};

export const AddPageRail: React.FC<Props> = ({ toggleAddPageHandler, setCurrentPage, iframeRef, addPage }) => {
  const { currentLayout } = useWebsiteLayoutContext();

  const { data: pageData } = usePagesQuery();
  const { data } = usePageQuery({
    variables: {
      id: addPage?.id as number,
    },
    skip: !addPage?.id,
  });
  const [selectedPage, setSelectedPage] = useState<PagesFragment>();
  const skipGuidancePages = [SCHEDULE_PAGE, REGISTRY_PAGE_TYPE, WEDDING_PAGE_TYPE];
  const previewItemRoutes = '/preview/pages/:pageId/:itemType';
  const match = useRouteMatch(previewItemRoutes);
  const matchCustomPage = useRouteMatch(addPagePreviewRoute);
  const client = useApolloClient();
  const history = useHistory();

  const isSinglePageSite = currentLayout === 'single-page';
  const pageOrSection = isSinglePageSite ? 'Section' : 'Page';

  const page = data?.page;

  useEffect(() => {
    setSelectedPage(addPage);
  }, [addPage]);

  const [updatePage] = useUpdatePageMutation();
  const onCloseHandler = () => {
    updatePage({
      variables: {
        id: selectedPage?.id as number,
        attributes: { show: false },
      },
    });

    setSelectedPage(undefined);
  };

  const selectedPageData = useCallback(
    () =>
      client.readQuery<PageQuery>({
        query: pageQuery,
        variables: {
          id: selectedPage?.id,
        },
      }),
    [selectedPage, client]
  );

  const saveItemHandler = useCallback(() => {
    toggleAddPageHandler();
    if (selectedPage) {
      if (selectedPage.routeName) {
        history.push(previewPageRoute(selectedPage.routeName));
      }
      updateIFrame(selectedPageData(), iframeRef);
      setCurrentPage(selectedPage);
    }
    setSelectedPage(undefined);
  }, [history, iframeRef, selectedPage, selectedPageData, setCurrentPage, toggleAddPageHandler]);

  const onBackHandler = () => {
    onCloseHandler();
    history.push(previewRoute);
  };

  useEffect(() => {
    if (page?.items.length) saveItemHandler();
  }, [page, saveItemHandler]);

  return (
    <>
      {matchCustomPage ? (
        <AddPage />
      ) : !selectedPage ? (
        <>
          <Header onClose={toggleAddPageHandler}>
            <div>{`Add ${pageOrSection}`}</div>
          </Header>
          <Content>
            <div className={styles.ideasContainer}>
              {pageData?.pages.map((page: PagesFragment) => {
                if (page.show || page.items.length || skipGuidancePages.includes(page.type)) {
                  return null;
                }
                return (
                  <HelpfulIdeaCard
                    key={page.id}
                    page={page}
                    addToSiteHandler={() => {
                      setSelectedPage(page);
                    }}
                  />
                );
              })}
            </div>
            <AddPageButton
              block
              children={`Add a Custom ${pageOrSection}`}
              onClick={() => {
                history.push(addPagePreviewRoute);
              }}
            />
          </Content>
        </>
      ) : (
        <>
          {match ? (
            // pages item routes
            <PageItemRoutes onSaveHandler={saveItemHandler} onBackHandler={onBackHandler} preview />
          ) : (
            <>
              <Header onClose={toggleAddPageHandler}>
                <div>{`Add ${selectedPage.title} ${pageOrSection}`}</div>
              </Header>
              <Content>
                <AddPageContentRail page={selectedPage} onClick={toggleAddPageHandler} iframeRef={iframeRef} />
              </Content>
            </>
          )}
        </>
      )}
    </>
  );
};

export default AddPageRail;
